/**
 * Dice Candidate Response Mapper
 * 
 * Transforms Dice API candidate responses into the existing Internal Candidate
 * List API format. This ensures the frontend receives an identical response
 * schema regardless of the data source.
 * 
 * Reusable for additional external sources (Monster, CareerBuilder, LinkedIn, etc.)
 */

/**
 * Normalizes skills from various Dice skill formats into a simple string array
 */
function firstPresentValue(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
}

function getPathValue(record, path) {
  if (!record || !path) return undefined;
  if (Object.prototype.hasOwnProperty.call(record, path)) return record[path];
  return String(path)
    .split('.')
    .reduce((value, key) => value?.[key], record);
}

function isLikelyInternalRecordId(value) {
  return /^[a-f0-9]{24}$/i.test(String(value ?? '').trim());
}

function isLikelyDiceProfileId(value) {
  const text = String(value ?? '').trim();
  return (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(text)
    || /^[a-f0-9]{40,}$/i.test(text)
  );
}

function isLikelyDiceCandidateId(value, candidate) {
  const text = String(value ?? '').trim();
  const knownCandidateId = firstPresentValue(
    candidate?.candidateId,
    candidate?.customFields?.diceProfileData?.candidateId,
  );

  return (
    (knownCandidateId && text === String(knownCandidateId).trim())
    || /^[a-f0-9]{40,}-\d+$/i.test(text)
  );
}

function findLikelyDiceProfileId(record) {
  const seen = new Set();
  const candidates = [];

  const visit = (value, path = '') => {
    if (value === null || value === undefined) return;
    if (typeof value === 'string' || typeof value === 'number') {
      const text = String(value).trim();
      const keyLooksRelevant = /(^|[._-])(dice|profile|source|external).*(id|guid|uuid)$|(^|[._-])(id|guid|uuid)$/i.test(path);
      if (text && keyLooksRelevant && isLikelyDiceProfileId(text)) {
        candidates.push({ path, value: text });
      }
      return;
    }
    if (typeof value !== 'object' || seen.has(value)) return;
    seen.add(value);
    Object.entries(value).forEach(([key, child]) => {
      visit(child, path ? `${path}.${key}` : key);
    });
  };

  visit(record);

  return candidates.find((candidate) => /dice|profile|source|external/i.test(candidate.path))?.value
    ?? candidates[0]?.value
    ?? '';
}

export function resolveDiceProfileId(candidate) {
  if (!candidate || typeof candidate !== 'object') return '';

  // Check if this is a Dice candidate
  const isDiceCandidate = (() => {
    const source = candidate?.sourceType
      ?? candidate?.profileSource
      ?? candidate?.selectedSource;
    const normalizedSources = Array.isArray(source) ? source : [source];
    return normalizedSources.some(
      (value) => String(value ?? '').trim().toLowerCase() === 'dice',
    );
  })();

  const explicitPaths = [
    'diceId',
    'diceID',
    'dice_id',
    'diceProfileId',
    'diceProfileID',
    'diceProfileGuid',
    'profileId',
    'profileID',
    'profileGuid',
    'sourceId',
    'sourceProfileId',
    'externalId',
    'externalProfileId',
    'customFields.diceId',
    'customFields.diceProfileId',
    'customFields.profileId',
    'customFields.externalProfileId',
    'customFields.diceProfileData.diceId',
    'customFields.diceProfileData.diceID',
    'customFields.diceProfileData.id',
    'customFields.diceProfileData._id',
    'customFields.diceProfileData.profileId',
    'customFields.diceProfileData.profileID',
    'customFields.diceProfileData.diceProfileId',
    'customFields.diceProfileData.diceProfileID',
    'customFields.diceProfileData.guid',
    'customFields.diceProfileData.uuid',
    'customFields.diceProfileData.externalId',
    'customFields.diceProfileData.candidateid',
    'customFields.diceProfileData.candidateId',
  ];

  const explicitDiceId = firstPresentValue(
    ...explicitPaths.map((path) => getPathValue(candidate, path)),
  );

  if (explicitDiceId && !isLikelyDiceCandidateId(explicitDiceId, candidate)) {
    return String(explicitDiceId);
  }

  const likelyNestedId = findLikelyDiceProfileId(candidate);
  if (likelyNestedId) return likelyNestedId;

  const genericId = firstPresentValue(candidate.id, candidate._id);
  
  // For Dice candidates, accept the ID even if it looks like an internal MongoDB ObjectId
  // For non-Dice candidates, reject IDs that look like internal record IDs
  if (genericId && (isDiceCandidate || !isLikelyInternalRecordId(genericId))) {
    return String(genericId);
  }
  
  return '';
}

export function normalizeDiceSkills(skills) {
  if (!skills) return [];
  
  if (Array.isArray(skills)) {
    return skills
      .map(skill => {
        // Handle Dice format: {skill: "courts", lastUsed: 2026}
        if (skill?.skill) return skill.skill;
        // Handle other object formats
        if (typeof skill === 'object' && skill !== null) {
          if (skill?.name) return skill.name;
          if (skill?.skillName) return skill.skillName;
          if (skill?.value) return skill.value;
        }
        // Handle string skills
        if (typeof skill === 'string') return skill;
        return null;
      })
      .filter(Boolean);
  }
  
  if (typeof skills === 'string') {
    return skills.split(',').map(s => s.trim()).filter(Boolean);
  }
  
  return [];
}

/**
 * Normalizes location from various Dice location formats
 */
function normalizeDiceLocation(candidate) {
  // Try locations array first (common in Dice)
  if (Array.isArray(candidate.locations) && candidate.locations.length > 0) {
    const loc = candidate.locations[0];
    if (typeof loc === 'string') return loc;
    if (loc?.region) return loc.region;
    if (loc?.city) return loc.city;
    if (loc?.name) return loc.name;
    if (loc?.location) return loc.location;
  }
  
  // Try direct location fields
  return (
    candidate.currentLocation ??
    candidate.location ??
    candidate.city ??
    candidate.region ??
    ''
  );
}

/**
 * Normalizes experience from various Dice experience formats
 */
function normalizeDiceExperience(candidate) {
  const exp = (
    candidate.totalExperience ??
    candidate.experience ??
    candidate.yearsOfExperience ??
    candidate.exp ??
    candidate.totalYearsOfExperience ??
    candidate.workExperienceYears
  );
  
  return exp != null && exp !== '' ? exp : null;
}

/**
 * Normalizes date from various Dice date formats
 */
function normalizeDiceDate(candidate) {
  return (
    candidate.dateLastUpdated ??
    candidate.updatedAt ??
    candidate.createdAt ??
    candidate.createdDate ??
    candidate.dateCreated ??
    null
  );
}

/**
 * Normalizes name from various Dice name formats
 */
function normalizeDiceName(candidate) {
  const parts = [
    candidate.firstName,
    candidate.middleName,
    candidate.lastName,
  ].filter(Boolean);
  
  if (parts.length > 0) return parts.join(' ');
  
  return (
    candidate.fullName ??
    candidate.name ??
    candidate.candidateName ??
    ''
  );
}

/**
 * Maps a single Dice candidate to the Internal Candidate format
 * 
 * @param {Object} diceCandidate - Raw Dice API candidate object
 * @param {number} index - Index for fallback ID generation
 * @returns {Object} Mapped candidate in Internal format
 */
export function mapDiceCandidateToInternal(diceCandidate, index = 0) {
  if (!diceCandidate || typeof diceCandidate !== 'object') {
    return {
      id: `dice-candidate-${index}`,
      diceId: '',
      candidateId: '',
      firstName: '',
      lastName: '',
      name: '-',
      designation: '-',
      currentLocation: '',
      location: '',
      exp: '',
      totalExperience: null,
      skills: [],
      createdAt: null,
      createdOn: '-',
      sourceType: 'dice',
      profileSource: 'dice',
    };
  }

  const fullName = normalizeDiceName(diceCandidate);
  const nameParts = fullName.split(' ');
  const firstName = nameParts[0] || '';
  const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
  
  const location = normalizeDiceLocation(diceCandidate);
  const experience = normalizeDiceExperience(diceCandidate);
  const createdOn = normalizeDiceDate(diceCandidate);
  const diceProfileId = resolveDiceProfileId(diceCandidate);
  const candidateId = diceCandidate.candidateId
    ?? diceCandidate.customFields?.diceProfileData?.candidateId
    ?? `dice-candidate-${index}`;
  
  // Extract skills from various possible fields
  const rawSkills = diceCandidate.skills ?? 
                    diceCandidate.technicalSkills ?? 
                    diceCandidate.primarySkills ??
                    diceCandidate.keySkills ??
                    [];
  
  const skills = normalizeDiceSkills(rawSkills);
  
  // Debug logging to verify transformation
  if (process.env.NODE_ENV === 'development') {
    console.log('[DiceMapper] Original skills:', rawSkills);
    console.log('[DiceMapper] Mapped skills:', skills);
    console.log('[DiceMapper] Dice IDs:', {
      id: diceProfileId,
      diceId: diceProfileId,
      candidateId,
    });
  }

  return {
    // Preserve original Dice fields for reference
    ...diceCandidate,
    
    // Map to Internal candidate schema. Keep the Dice profile id as the primary id.
    id: diceProfileId,
    diceId: diceProfileId,
    candidateId,
    
    // Name fields
    firstName,
    lastName,
    middleName: diceCandidate.middleName || '',
    name: fullName || '-',
    fullName: fullName || '',
    
    // Professional info
    designation: diceCandidate.currentJobTitle ?? diceCandidate.jobTitle ?? diceCandidate.designation ?? diceCandidate.currentDesignation ?? '-',
    currentDesignation: diceCandidate.currentJobTitle ?? diceCandidate.jobTitle ?? diceCandidate.designation ?? diceCandidate.currentDesignation ?? '',
    jobTitle: diceCandidate.currentJobTitle ?? diceCandidate.jobTitle ?? diceCandidate.designation ?? '',
    
    // Location
    currentLocation: location || '',
    location: location || '',
    city: diceCandidate.city ?? diceCandidate.locations?.[0]?.city ?? '',
    region: diceCandidate.region ?? diceCandidate.locations?.[0]?.region ?? '',
    
    // Experience
    totalExperience: experience,
    experience: experience,
    yearsOfExperience: experience,
    exp: experience != null && experience !== '' ? `${experience} yrs` : '',
    
    // Skills
    skills,
    technicalSkills: skills,
    primarySkills: skills,
    
    // Dates
    createdAt: createdOn,
    createdOn: createdOn ? String(createdOn) : '-',
    updatedAt: createdOn,
    dateLastUpdated: createdOn,
    
    // Source identification
    sourceType: 'dice',
    profileSource: 'dice',
    selectedSource: ['dice'],
    
    // Additional fields with defaults
    email: diceCandidate.email ?? diceCandidate.emailAddress ?? '',
    phone: diceCandidate.phone ?? diceCandidate.phoneNumber ?? diceCandidate.mobile ?? '',
    summary: diceCandidate.summary ?? diceCandidate.bio ?? diceCandidate.description ?? '',
    resume: diceCandidate.resume ?? diceCandidate.resumeUrl ?? diceCandidate.cvUrl ?? '',
    
    // Status and metadata
    isActive: true,
    isDeleted: false,
  };
}

/**
 * Maps an array of Dice candidates to the Internal Candidate format
 * 
 * @param {Array} diceCandidates - Array of raw Dice API candidate objects
 * @returns {Array} Array of mapped candidates in Internal format
 */
export function mapDiceCandidatesToInternal(diceCandidates) {
  if (!Array.isArray(diceCandidates)) {
    return [];
  }
  
  return diceCandidates.map((candidate, index) => 
    mapDiceCandidateToInternal(candidate, index)
  );
}

/**
 * Transforms a Dice API response to match the existing Candidate List API response format
 * 
 * @param {Object} diceResponse - Raw Dice API response
 * @param {Object} originalResponseStructure - The expected response structure from Internal API
 * @returns {Object} Transformed response matching Internal API format
 */
export function transformDiceResponseToInternalFormat(diceResponse, originalResponseStructure = {}) {
  if (!diceResponse || typeof diceResponse !== 'object') {
    return {
      status: 'success',
      data: {
        fields: [],
        actions: [],
        columnActions: [],
        count: 0,
        data: [],
      },
    };
  }

  // Extract candidates array from various possible response structures
  const diceCandidates = Array.isArray(diceResponse) 
    ? diceResponse 
    : diceResponse?.data?.data ?? 
      diceResponse?.data?.applicant ?? 
      diceResponse?.data?.candidates ?? 
      diceResponse?.data?.items ?? 
      diceResponse?.items ?? 
      diceResponse?.candidates ?? 
      diceResponse?.records ?? 
      [];

  // Map Dice candidates to Internal format
  const mappedCandidates = mapDiceCandidatesToInternal(diceCandidates);

  // Extract count from various possible locations
  const count = (
    diceResponse?.data?.count?.searchCount ??
    diceResponse?.data?.count?.total ??
    diceResponse?.count?.searchCount ??
    diceResponse?.count?.total ??
    diceResponse?.total ??
    diceResponse?.totalCount ??
    mappedCandidates.length
  );

  // Preserve existing metadata from the original response structure
  return {
    status: diceResponse?.status ?? 'success',
    data: {
      // Preserve fields, actions, columnActions from original structure
      fields: originalResponseStructure?.fields ?? diceResponse?.data?.fields ?? diceResponse?.fields ?? [],
      actions: originalResponseStructure?.actions ?? diceResponse?.data?.actions ?? diceResponse?.actions ?? [],
      columnActions: originalResponseStructure?.columnActions ?? diceResponse?.data?.columnActions ?? diceResponse?.columnActions ?? [],
      
      // Count and mapped data
      count: Number(count) || 0,
      data: mappedCandidates,
      
      // Preserve any additional metadata
      ...(diceResponse?.data?.actionRules && { actionRules: diceResponse.data.actionRules }),
      ...(diceResponse?.data?.tabs && { tabs: diceResponse.data.tabs }),
      ...(diceResponse?.data?.tabField && { tabField: diceResponse.data.tabField }),
    },
  };
}

/**
 * Checks if a response contains Dice candidates
 * 
 * @param {Array|Object} response - API response to check
 * @returns {boolean} True if response contains Dice candidates
 */
export function isDiceResponse(response) {
  if (!response) return false;
  
  const candidates = Array.isArray(response) 
    ? response 
    : response?.data?.data ?? 
      response?.data?.applicant ?? 
      response?.data?.candidates ?? 
      response?.data?.items ?? 
      response?.items ?? 
      [];
  
  if (!Array.isArray(candidates) || candidates.length === 0) {
    return false;
  }
  
  // Check first few candidates for Dice source indicators
  const sampleSize = Math.min(candidates.length, 5);
  for (let i = 0; i < sampleSize; i++) {
    const candidate = candidates[i];
    const sourceType = candidate?.sourceType ?? candidate?.profileSource ?? '';
    if (String(sourceType).toLowerCase() === 'dice') {
      return true;
    }
  }
  
  return false;
}

export default {
  mapDiceCandidateToInternal,
  mapDiceCandidatesToInternal,
  transformDiceResponseToInternalFormat,
  isDiceResponse,
};
